import React from "react";
import { BasisProvider, defaultTheme } from "basis";
import { Helmet } from "react-helmet";
import BrowserSupport from "./components/BrowserSupport";

import { ErrorBoundary, logger, SOURCE } from "./core";
import { getLaunchDarklyUserContext } from "./utils/getLaunchDarklyUserContext";

import "./crypto.js";
import "./reset.css";

// initiate logger
logger.init({ source: SOURCE.SPA });

export const LD_READY_EVENT = "launchdarkly:user-ready";

const LaunchDarklyWrapper = ({ children }) => {
  const [LDProvider, setLDProvider] = React.useState(null);
  const initialised = React.useRef(false);

  React.useEffect(() => {
    const initLD = async (trigger) => {
      if (initialised.current) return;
      const config = require("./core/config");
      const clientSideID = config.launchDarkly && config.launchDarkly.clientId;
      if (!clientSideID) {
        return;
      }

      const userContext = getLaunchDarklyUserContext();
      if (!userContext) {
        return;
      }

      initialised.current = true;
      try {
        const { asyncWithLDProvider } = await import("launchdarkly-react-client-sdk");
        const provider = await asyncWithLDProvider({
          clientSideID,
          user: userContext,
        });
        setLDProvider(() => provider);
      } catch (e) {
        initialised.current = false;
        logger.error("Failed to initialize LaunchDarkly:", e);
      }
    };

    const onReady = () => {
      initLD("event");
    };
    window.addEventListener(LD_READY_EVENT, onReady);
    // Attempt immediately in case auth already completed before this mounted
    initLD("mount");
    return () => window.removeEventListener(LD_READY_EVENT, onReady);
  }, []);

  if (!LDProvider) return children;
  return <LDProvider>{children}</LDProvider>;
};

export const wrapRootElement = ({ element }) => (
  <ErrorBoundary>
    <Helmet>
      <meta name="referrer" content="origin" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Latitude Partner Hub</title>
      <link
        href="https://fonts.googleapis.com/css?family=Montserrat:600|Roboto:300,500,700&display=swap"
        rel="stylesheet"
      ></link>
      <link
        href="https://global.oktacdn.com/okta-signin-widget/6.2.0/css/okta-sign-in.min.css"
        type="text/css"
        rel="stylesheet"
      />
    </Helmet>
    <BrowserSupport />

    <BasisProvider theme={defaultTheme}>
      <LaunchDarklyWrapper>{element}</LaunchDarklyWrapper>
    </BasisProvider>
  </ErrorBoundary>
);
